import { DataTableEmptyCell } from "@bcc/components/data-table";
import type { DataTableRow } from "@bcc/components/data-table/data-table.types";
import type { PurchaseOrder } from "@shared/types";
import type { CellContext } from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { AGE_CRITICAL_HOURS, AGE_WARNING_HOURS, AGE_YIELD_HOURS, DEFAULT_ORDER_STATUS } from "../constants";
import { computeOrderAge } from "../utils/compute-order-age";

const URGENCY_CLASSES = {
  critical: "text-red-700 dark:text-red-400 font-bold",
  warning: "text-red-600 dark:text-red-500",
  yield: "text-amber-600 dark:text-amber-400",
  ok: "text-green-600 dark:text-green-400",
  disabled: "text-muted-foreground",
} as const;

type UrgencyLevel = keyof typeof URGENCY_CLASSES;

function getUrgencyLevel(hours: number, status: string): UrgencyLevel {
  if (status !== DEFAULT_ORDER_STATUS) return "disabled";
  if (hours < AGE_CRITICAL_HOURS) return "critical";
  if (hours < AGE_WARNING_HOURS) return "warning";
  if (hours < AGE_YIELD_HOURS) return "yield";
  if (hours < 0) return "ok";
  return "disabled";
}

export const OrderAgeCell = (props: CellContext<DataTableRow<PurchaseOrder>, unknown>) => {
  const { t } = useTranslation();
  const order = props.row.original;
  const ageResult = computeOrderAge(order);

  if (!ageResult) return <DataTableEmptyCell />;
  if (!ageResult.isFulfillmentDatePassed || !ageResult.maxDate) {
    return <DataTableEmptyCell>0</DataTableEmptyCell>;
  }

  const { minutes, maxDate } = ageResult;
  const urgency = getUrgencyLevel(minutes / 60, order.orderStatus ?? "");

  const displayText = t("dates.distance", {
    date: {
      start: parseISO(order.expectedFulfillmentDate ?? order.createdAt),
      end: maxDate,
    },
  });

  return <span className={URGENCY_CLASSES[urgency]}>{displayText}</span>;
};
